import { ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

export type FeedbackFlag = 'at_risk' | 'neutral' | 'happy' | null;
export type FeedbackType = 'post_delivery' | 'pulse';

export interface FeedbackRecord {
  id: number;
  order_id: number;
  order_no: string | null;
  company_id: number;
  company_name: string | null;
  customer_id: number;
  customer_name: string | null;
  type: FeedbackType;
  overall_rating: number | null;
  quality_rating: number | null;
  delivery_rating: number | null;
  nps_score: number | null;
  comment: string | null;
  flag: FeedbackFlag;
  survey_sent_at: string | null;
  reminder_sent_at: string | null;
  responded_at: string | null;
  created_at: string | null;
}

export interface FeedbackSummary {
  sent: number;
  responded: number;
  response_rate: number;
  avg_overall: number | null;
  avg_quality: number | null;
  avg_delivery: number | null;
  avg_nps: number | null;
  at_risk: number;
  neutral: number;
  happy: number;
}

export interface DecliningAccount {
  company_id: number;
  company_name: string;
  at_risk_count: number;
  responded_count: number;
  avg_overall: number | null;
}

export interface FeedbackListFilters {
  company_id?: number | null;
  flag?: FeedbackFlag | '';
  type?: FeedbackType | '';
  from?: string | null;
  to?: string | null;
  responded_only?: boolean;
  page?: number;
  per_page?: number;
}

export function useCustomerFeedback() {
  const orderFeedback = ref<FeedbackRecord | null>(null);
  const orderFeedbackLoading = ref(false);

  const list = ref<FeedbackRecord[]>([]);
  const listTotal = ref(0);
  const listLoading = ref(false);

  const summary = ref<FeedbackSummary | null>(null);
  const summaryLoading = ref(false);

  const declining = ref<DecliningAccount[]>([]);
  const decliningLoading = ref(false);

  async function fetchForOrder(orderId: number): Promise<FeedbackRecord | null> {
    orderFeedbackLoading.value = true;
    try {
      // TEMP (AB#19525): backend route GET /api/admin/orders/{id}/customer-feedback is
      // not implemented yet and returns 404 (route not found). Call commented out to
      // stop the failing request until the backend endpoint is available.
      // const { data } = await http.get(`/orders/${orderId}/customer-feedback`);
      // orderFeedback.value = data?.result?.feedback ?? null;
      void orderId;
      orderFeedback.value = null;
      return orderFeedback.value;
    } catch (err) {
      orderFeedback.value = null;
      return null;
    } finally {
      orderFeedbackLoading.value = false;
    }
  }

  async function fetchList(filters: FeedbackListFilters = {}): Promise<void> {
    listLoading.value = true;
    try {
      const { data } = await http.get('/customer-feedback', { params: filters });
      list.value = data?.result?.items ?? [];
      listTotal.value = Number(data?.result?.total ?? 0);
    } finally {
      listLoading.value = false;
    }
  }

  async function fetchSummary(filters: Omit<FeedbackListFilters, 'page' | 'per_page'> = {}): Promise<void> {
    summaryLoading.value = true;
    try {
      const { data } = await http.get('/customer-feedback/summary', { params: filters });
      summary.value = data?.result ?? null;
    } finally {
      summaryLoading.value = false;
    }
  }

  async function fetchDecliningAccounts(days = 60, limit = 10): Promise<void> {
    decliningLoading.value = true;
    try {
      const { data } = await http.get('/customer-feedback/declining-accounts', {
        params: { days, limit }
      });
      declining.value = data?.result?.items ?? [];
    } finally {
      decliningLoading.value = false;
    }
  }

  async function exportCsv(filters: Omit<FeedbackListFilters, 'page' | 'per_page'> = {}): Promise<void> {
    const response = await http.get('/customer-feedback/export.csv', {
      params: filters,
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename =
      extractFilenameFromContentDisposition(response.headers?.['content-disposition']) ||
      `customer-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  return {
    orderFeedback,
    orderFeedbackLoading,
    list,
    listTotal,
    listLoading,
    summary,
    summaryLoading,
    declining,
    decliningLoading,
    fetchForOrder,
    fetchList,
    fetchSummary,
    fetchDecliningAccounts,
    exportCsv
  };
}

function extractFilenameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const match = header.match(/filename="?([^"]+)"?/);
  return match ? match[1] : null;
}
